// Write-side hooks. Each returns a `useMutation` and invalidates queries
// touched by the write.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  EnrichedFeature,
  IngestResult,
  ItemCategory,
  PromptKey,
  PromptRow,
  Release,
  ReleaseBuild,
  ReleaseItem,
} from "@release-hub/shared";
import { api, apiRaw } from "./client";

/** Invalidate every edit-history query. Called from every write mutation
 *  so an open HistoryDrawer refreshes as soon as its underlying entity is
 *  edited. Cheap — history endpoints are only fetched when a drawer is
 *  actually open. */
function invalidateHistory(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["edit-history"] });
}


/** POST /api/history/:id/revert — undoes a single 'update' entry by applying
 *  its `before` values back through patchRow. Invalidates every entity
 *  cache since we don't know at call-site which entity was touched. */
export function useRevertHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (historyId: string) =>
      api<{ ok: true; id: string; updated_fields: string[]; reverted_from: string; skipped: string[] }>(
        `/api/history/${historyId}/revert`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Belt-and-braces: revert could touch a feature, release, item, or prompt.
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      qc.invalidateQueries({ queryKey: ["prompts"] });
      invalidateHistory(qc);
    },
  });
}

/** PATCH /api/prompts/:key — passing `content: null` reverts to default. */
export function useSavePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, content }: { key: PromptKey; content: string | null }) =>
      api<PromptRow>(`/api/prompts/${key}`, {
        method: "PATCH",
        body: { content },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts"] });
      invalidateHistory(qc);
    },
  });
}

// ─── Features ────────────────────────────────────────────────────────

/** PATCH any editable feature_entries field. Body is the partial update. */
export function usePatchFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<EnrichedFeature> }) =>
      api(`/api/feature-entries/${id}`, {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
      qc.invalidateQueries({ queryKey: ["feature-entries", id] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      invalidateHistory(qc);
    },
  });
}

/** Fields an "Autofill from notes" blurb can propose for a feature launch.
 *  Every key optional — the extractor omits anything the blurb doesn't
 *  support, so the returned object is exactly the proposed change set. */
export interface FeatureAutofillFields {
  name?: string;
  pitch?: string;
  feature_family?: string;
  audience?: string;
  announcement_narrative?: string;
  known_gaps?: string[];
  web_note?: string;
  mobile_note?: string;
  beta?: boolean;
  surface?: string[];
  announcement_state?: "EA" | "GA";
  ea_date?: string;
  ga_date?: string;
}

/** Extract feature fields from a pasted blurb. Read-only — returns a proposed
 *  partial; nothing is written until the editor accepts and PATCHes. */
export function useAutofillFeaturePreview() {
  return useMutation({
    mutationFn: ({ id, blurb }: { id: string; blurb: string }) =>
      api<{ ok: true; fields: FeatureAutofillFields }>(
        `/api/feature-entries/${id}/autofill-preview`,
        { method: "POST", body: { blurb } },
      ),
  });
}

// ─── Feature families ────────────────────────────────────────────────

export function useCreateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ ok: true; id: string; name: string }>("/api/feature-families", {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feature-families"] }),
  });
}

export function useRenameFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(`/api/feature-families/${id}`, {
        method: "PATCH",
        body: { name },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-families"] });
      // Cascade-updated feature_entries need a refresh too.
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
    },
  });
}

export function useDeleteFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/feature-families/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-families"] });
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
    },
  });
}

/** Create a new feature draft, seeded from a flag id. Server derives the
 *  human name from the flag id; caller PATCHes name/pitch/etc afterward. */
export function useCreateFeatureFromFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flagId: string) =>
      api<{ ok: true; id: string }>("/api/feature-entries", {
        method: "POST",
        body: { seed_from_flag_id: flagId },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feature-entries"] }),
  });
}

/** Full launch payload from the guided "New feature launch" modal. The
 *  server requires flag_ids non-empty; everything else is optional and
 *  falls back to draft defaults. */
export interface CreateFeatureLaunchInput {
  flag_ids: string[];
  name?: string;
  pitch?: string;
  feature_family?: string | null;
  owner_email?: string | null;
  surface?: string[];
  beta?: boolean;
  announcement_state?: "EA" | "GA" | null;
  ea_date?: string | null;
  ga_date?: string | null;
  web_note?: string | null;
  mobile_note?: string | null;
  known_gaps?: string[];
  /** Freeform notes to draft the fields from. When present, the draft is
   *  created as `seeding` and an LLM pass fills fields in the background —
   *  the caller navigates to the detail page immediately (which shows a
   *  seeding skeleton until the pass clears). */
  blurb?: string;
}

export function useCreateFeatureLaunch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blurb, ...input }: CreateFeatureLaunchInput) => {
      const text = blurb?.trim() ?? "";
      const created = await api<{ ok: true; id: string }>("/api/feature-entries", {
        method: "POST",
        body: { ...input, will_seed: text.length > 0 },
      });
      // Kick off the LLM field-seed in its own request but DON'T await it —
      // create returns instantly, the caller navigates, and the detail page
      // polls until seeding_at clears. A real request keeps CPU allocated on
      // Cloud Run for the whole (slow) extract. Mirrors the release convert.
      if (text) {
        void api(`/api/feature-entries/${created.id}/seed`, {
          method: "POST",
          body: { blurb: text },
        }).catch(() => {});
      }
      return created;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feature-entries"] }),
  });
}

// ─── Staging ─────────────────────────────────────────────────────────

/** POST /api/staging/refresh — pulls latest GitHub releases into the cache.
 *  Editor-only. Idempotent; invalidates every staging query. */
export function useRefreshStaging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; results: IngestResult[] }>("/api/staging/refresh", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staging"] });
      qc.invalidateQueries({ queryKey: ["releases"] });
      // The Timeline's uncurated-releases drawer reads from the feed, so a
      // fresh pull needs to bust it too.
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}


/** PATCH /api/github-releases/:id — toggle the `ignored` flag on a raw
 *  github_releases row. Editor-only. */
export function usePatchGithubRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ignored }: { id: number; ignored: boolean }) =>
      api<{ ok: true; id: number; ignored: boolean }>(
        `/api/github-releases/${id}`,
        { method: "PATCH", body: { ignored } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staging"] });
      qc.invalidateQueries({ queryKey: ["releases"] });
      // Ignoring removes the build from the Timeline's uncurated rollup.
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** Create ONE curated release from a set of selected raw builds. Converts
 *  the first (LLM-seeds narrative + items), then appends the rest via
 *  /builds so they all wrap a single release. When more than one build is
 *  selected the concatenated bodies are handed to the LLM as a combined
 *  summary (source_body_override) so the narrative reflects every selected
 *  build, not just the first. Editor-only server-side. Returns the new id. */
export function useCreateReleaseFromBuilds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (builds: ReleaseBuild[]) => {
      if (builds.length === 0) throw new Error("Select at least one build.");
      const [first, ...rest] = builds;
      const combinedBody =
        builds.length > 1
          ? builds
              .map((b) => `## ${b.tag_name}\n\n${b.body ?? ""}`.trim())
              .join("\n\n")
          : null;
      const created = await api<{
        ok: true;
        id: string;
        github_release_ids: number[];
      }>("/api/releases", {
        method: "POST",
        body: {
          github_release_id: first.github_release_id,
          source_body_override: combinedBody,
        },
      });
      // Append the remaining picks. The convert step may have auto-paired an
      // iOS/Android sibling, so drop any id it already wrapped.
      const restIds = rest
        .map((b) => b.github_release_id)
        .filter((id) => !created.github_release_ids.includes(id));
      if (restIds.length > 0) {
        await api(`/api/releases/${created.id}/builds`, {
          method: "POST",
          body: { github_release_ids: restIds },
        });
      }
      // Kick off LLM seeding (narrative + items) in its own request, but DON'T
      // await it — Convert returns instantly and the release page polls until
      // seeding_at clears. Running it as a real request keeps CPU allocated on
      // Cloud Run for the whole (slow) seed.
      void api(`/api/releases/${created.id}/seed`, { method: "POST" }).catch(() => {});
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staging"] });
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** POST /api/releases/planned — schedule a release that hasn't shipped yet
 *  (no GitHub builds). Server defaults build_cut_date to the Wednesday
 *  before the target for mobile when the field is omitted; pass null for
 *  "no build cut". Editor-only. */
export function useCreatePlannedRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      surface: "mobile" | "web";
      target_date: string; // YYYY-MM-DD
      build_cut_date?: string | null;
    }) =>
      api<{ ok: true; id: string; build_cut_date: string | null }>(
        "/api/releases/planned",
        { method: "POST", body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** Mark several raw github_releases ignored at once — the bulk form of
 *  usePatchGithubRelease, for clearing noise out of the Uncurated table.
 *  Editor-only. */
export function useIgnoreBuilds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (githubReleaseIds: number[]) => {
      await Promise.all(
        githubReleaseIds.map((id) =>
          api(`/api/github-releases/${id}`, {
            method: "PATCH",
            body: { ignored: true },
          }),
        ),
      );
      return { count: githubReleaseIds.length };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staging"] });
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** PATCH /api/releases/:id — hide/unhide a curated release from the
 *  Timeline. Non-destructive; editor-only. */
export function useSetReleaseHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) =>
      api(`/api/releases/${id}`, { method: "PATCH", body: { hidden } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

/** POST /api/releases/:survivorId/merge — fold `absorbId` into the survivor
 *  (its builds + items move over; the absorbed release is deleted). */
export function useMergeRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ survivorId, absorbId }: { survivorId: string; absorbId: string }) =>
      api<{ ok: true; id: string; absorbed: string }>(
        `/api/releases/${survivorId}/merge`,
        { method: "POST", body: { absorb_id: absorbId } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["releases"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

// ─── Releases ────────────────────────────────────────────────────────

function invalidateReleases(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ["releases"] });
  qc.invalidateQueries({ queryKey: ["feed"] });
  if (id) qc.invalidateQueries({ queryKey: ["releases", "detail", id] });
  // Any release or release_item mutation should refresh an open history
  // drawer — item edits show up under the parent release's history too.
  invalidateHistory(qc);
}

export function usePatchRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Release> }) =>
      api(`/api/releases/${id}`, { method: "PATCH", body: patch }),
    onSuccess: (_, { id }) => invalidateReleases(qc, id),
  });
}

/** A proposed release item from an "Autofill from notes" blurb — the same
 *  shape the LLM item extractor emits, minus the DB ids. */
export interface AutofillReleaseItem {
  category: string;
  text: string;
  parity_notes?: string | null;
  linked_flag_ids: string[];
  linear_ticket_ids: string[];
}

/** Extract narrative + items from a pasted blurb. Read-only — returns
 *  proposed values; the editor diffs and applies (narrative via PATCH,
 *  items via useSetReleaseItems). */
export function useAutofillReleasePreview() {
  return useMutation({
    mutationFn: ({ id, blurb }: { id: string; blurb: string }) =>
      api<{ ok: true; narrative: string | null; items: AutofillReleaseItem[] }>(
        `/api/releases/${id}/autofill-preview`,
        { method: "POST", body: { blurb } },
      ),
  });
}

/** Replace all items on a release with a caller-supplied set. Backs the
 *  block-level "accept proposed items" apply step. */
export function useSetReleaseItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: AutofillReleaseItem[] }) =>
      api<{ ok: true; count: number }>(`/api/releases/${id}/set-items`, {
        method: "POST",
        body: { items },
      }),
    onSuccess: (_, { id }) => invalidateReleases(qc, id),
  });
}

export function usePublishRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      api(`/api/releases/${id}/${publish ? "publish" : "unpublish"}`, {
        method: "POST",
      }),
    onSuccess: (_, { id }) => invalidateReleases(qc, id),
  });
}

export function useDeleteRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/releases/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateReleases(qc),
  });
}

/** Append one or more github_releases to an existing release. Symmetric
 *  to Split — used to merge pre-release tags into the shipped release.
 *  Server auto-unignores any ignored ones. 409 if any target is already
 *  wrapped by a different release. */
export function useAddBuilds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ghIds }: { id: string; ghIds: number[] }) =>
      api<{
        ok: true;
        id: string;
        added: number[];
        already_wrapped: number[];
      }>(`/api/releases/${id}/builds`, {
        method: "POST",
        body: { github_release_ids: ghIds },
      }),
    onSuccess: (_, { id }) => {
      invalidateReleases(qc, id);
      qc.invalidateQueries({ queryKey: ["staging"] });
    },
  });
}

/** Split a multi-build release into N independent clones (one per build).
 *  Original keeps its first build; server returns the new_release_ids for
 *  the clones. Deep-copies items + all editable fields. */
export function useSplitRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; original_id: string; new_release_ids: string[] }>(
        `/api/releases/${id}/split`,
        { method: "POST" },
      ),
    onSuccess: (_, id) => invalidateReleases(qc, id),
  });
}

export function useRegenerateNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; narrative: string | null }>(
        `/api/releases/${id}/regenerate-narrative`,
        { method: "POST" },
      ),
    onSuccess: (_, id) => invalidateReleases(qc, id),
  });
}

export function useRegenerateItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; items_seeded: number }>(
        `/api/releases/${id}/regenerate-items`,
        { method: "POST" },
      ),
    onSuccess: (_, id) => invalidateReleases(qc, id),
  });
}

// ─── Release items ───────────────────────────────────────────────────

export function useAddItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      releaseId,
      body,
    }: {
      releaseId: string;
      body: {
        category?: ItemCategory;
        text?: string;
        parity_notes?: string | null;
        linked_flag_ids?: string[];
        linear_ticket_ids?: string[];
        rollout_override?: "ea" | "ga" | null;
      };
    }) =>
      api<{ ok: true; id: string }>(`/api/releases/${releaseId}/items`, {
        method: "POST",
        body,
      }),
    onSuccess: (_, { releaseId }) => invalidateReleases(qc, releaseId),
  });
}

export function usePatchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
      releaseId,
    }: {
      id: string;
      patch: Partial<ReleaseItem>;
      releaseId: string;
    }) => api(`/api/release-items/${id}`, { method: "PATCH", body: patch }),
    onSuccess: (_, { releaseId }) => invalidateReleases(qc, releaseId),
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, releaseId }: { id: string; releaseId: string }) =>
      api(`/api/release-items/${id}`, { method: "DELETE" }),
    onSuccess: (_, { releaseId }) => invalidateReleases(qc, releaseId),
  });
}

// ─── Media ───────────────────────────────────────────────────────────

export function useUploadMedia() {
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await apiRaw("/api/media", file, {
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
      });
      return (await res.json()) as { url: string; bytes: number };
    },
  });
}

export function useDeleteFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/feature-entries/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      invalidateHistory(qc);
    },
  });
}

export function usePublishFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      api(`/api/feature-entries/${id}/${publish ? "publish" : "unpublish"}`, {
        method: "POST",
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
      qc.invalidateQueries({ queryKey: ["feature-entries", id] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      invalidateHistory(qc);
    },
  });
}

/** Force-refresh the server-side squapi caches (flags + workspaces).
 *  Blocks until fresh data lands, then invalidates every React Query
 *  that consumes flag or workspace data. Use when you know squapi
 *  changed and don't want to wait out the stale window. */
export function useRefreshSquapiCaches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; flags: number; workspaces: number }>(
        "/api/admin/refresh-caches",
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["squapi", "flags"] });
      qc.invalidateQueries({ queryKey: ["squapi", "workspaces"] });
      qc.invalidateQueries({ queryKey: ["feature-entries"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
